import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, RotateCcw, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { RollbackButton } from "@/components/RollbackButton";
import { PromoteButton } from "@/components/PromoteButton";
import { ShareLinkButton } from "@/components/ShareLinkButton";
import { StatusPill, type DeploymentStatus } from "@/components/StatusPill";
import { EnvBadge, type Env } from "@/components/EnvBadge";
import { CommitRef } from "@/components/CommitRef";
import { RelativeTime } from "@/components/RelativeTime";
import { BuildLogs } from "@/components/BuildLogs";
import { RuntimeLogs } from "@/components/RuntimeLogs";
import { DeploymentChecks } from "@/components/DeploymentChecks";
import { duration, shortSha } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Deployment {
  id: string;
  status: DeploymentStatus;
  env: Env;
  branch: string;
  commitSha: string;
  commitMessage?: string;
  authorEmail?: string;
  envResolvedFrom: string;
  createdAt: string;
  readyAt?: string | null;
  durationMs?: number | null;
  url?: string | null;
  aliases?: string[];
  failedReason?: string | null;
  outputPath?: string | null;
  framework?: "vite" | "next";
  cacheStatus?: "hit" | "miss" | "skipped" | null;
}

export function DeploymentDetail() {
  const { org, project, id } = useParams<{ org: string; project: string; id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["deployment", id],
    queryFn: () => api.get<{ deployment: Deployment }>(`/deployments/${id}`).then((r) => r.deployment),
    refetchInterval: (data) => {
      const dep = data.state.data;
      return dep && (dep.status === "building" || dep.status === "queued") ? 4000 : false;
    },
  });
  const d = q.data;

  // Redeploy uses the SAME env as the current deployment, so prod redeploys
  // prod, preview redeploys preview, etc. The redeploy endpoint resolves the
  // branch HEAD itself, so we don't need to pass a SHA.
  const redeploy = useMutation({
    mutationFn: () =>
      api.post<{ deploymentId: string }>(`/orgs/${org}/projects/${project}/redeploy`, {
        env: d?.env === "preview" ? "production" : (d?.env ?? "production"),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["deployments", org, project] });
      nav(`/orgs/${org}/projects/${project}/deployments/${r.deploymentId}`);
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/deployments/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deployment", id] }),
  });

  return (
    <div className="container max-w-screen-xl py-6">
      <Link to={`/orgs/${org}/projects/${project}/deployments`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-3 w-3" /> All deployments
      </Link>

      <div className="of-card mb-5 overflow-hidden">
        <div className="px-5 py-4 flex items-center gap-4 flex-wrap">
          <StatusPill status={d?.status ?? "queued"} size="sm" />
          <EnvBadge env={d?.env ?? "preview"} />
          <div className="flex-1 min-w-0">
            {d ? (
              <CommitRef branch={d.branch} sha={d.commitSha} message={d.commitMessage ?? null} />
            ) : (
              <div className="h-4 w-64 bg-subtle rounded animate-pulse" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {d?.url && d.status === "ready" && (
              <Button asChild variant="default" size="sm">
                <a href={d.url} target="_blank" rel="noreferrer">
                  Visit <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            )}
            {d && (d.status === "queued" || d.status === "building") && (
              <Button variant="default" size="sm" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                {cancel.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />} Cancel
              </Button>
            )}
            {d && d.status === "ready" && org && project && (d.env === "production" || d.env === "staging" || d.env === "development") && (
              <RollbackButton
                orgSlug={org}
                projectSlug={project}
                env={d.env as "production" | "staging" | "development"}
                targetDeploymentId={d.id}
                label="Pin here"
              />
            )}
            {d && d.status === "ready" && org && project && (
              <PromoteButton
                orgSlug={org}
                projectSlug={project}
                deploymentId={d.id}
                currentEnv={d.env}
              />
            )}
            {d && d.status === "ready" && (
              <ShareLinkButton deploymentId={d.id} deploymentUrl={d.url ?? null} />
            )}
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={redeploy.isPending}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("[deploy detail] Redeploy clicked", { org, project, env: d?.env });
                redeploy.mutate();
              }}
            >
              {redeploy.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Redeploy
            </Button>
          </div>
        </div>
        {redeploy.isError && (
          <div className="px-5 py-2 border-t border-border-muted bg-danger-subtle text-xs text-danger">
            {(redeploy.error as ApiError)?.message ?? "redeploy failed"}
          </div>
        )}
        {d?.failedReason === "superseded" ? (
          <div className="px-5 py-2 border-t border-border-muted bg-subtle/60 text-xs text-muted-foreground">
            Superseded — a newer commit on <span className="font-mono">{d.branch}</span> replaced this build before it finished.
          </div>
        ) : d?.failedReason ? (
          <div className="px-5 py-2 border-t border-border-muted bg-danger-subtle text-xs text-danger font-mono break-words">
            {d.failedReason}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
        <Tabs defaultValue="logs">
          <TabsList className="flex max-w-full overflow-x-auto scrollbar-none">
            <TabsTrigger value="logs">Build logs</TabsTrigger>
            <TabsTrigger value="runtime">Runtime</TabsTrigger>
            <TabsTrigger value="checks">Checks</TabsTrigger>
            <TabsTrigger value="diff">Changes</TabsTrigger>
          </TabsList>
          <TabsContent value="logs">
            {id && <BuildLogs deploymentId={id} />}
          </TabsContent>
          <TabsContent value="runtime">
            {id && <RuntimeLogs deploymentId={id} />}
          </TabsContent>
          <TabsContent value="checks">
            {id && <DeploymentChecks deploymentId={id} ready={d?.status === "ready"} />}
          </TabsContent>
          <TabsContent value="diff">
            <div className="of-card p-10 text-center text-sm text-muted-foreground">
              File diff view (planned).
            </div>
          </TabsContent>
        </Tabs>

        <aside className="of-card divide-y divide-border-muted text-sm h-fit">
          <Meta label="Deployment ID" value={d?.id ? shortSha(d.id, 12) : "—"} mono />
          <Meta label="Environment" value={d?.env ?? "—"} />
          <Meta label="Resolved from" value={d?.envResolvedFrom ?? "—"} mono />
          <Meta label="Branch" value={d?.branch ?? "—"} mono />
          <Meta label="Commit" value={d ? shortSha(d.commitSha) : "—"} mono />
          <Meta label="Author" value={d?.authorEmail ?? "—"} mono />
          <Meta label="Created" value={d?.createdAt ? <RelativeTime value={d.createdAt} /> : "—"} />
          <Meta label="Ready" value={d?.readyAt ? <RelativeTime value={d.readyAt} /> : "—"} />
          <Meta label="Duration" value={duration(d?.durationMs ?? null)} />
          <Meta
            label="Build cache"
            value={
              d?.cacheStatus === "hit" ? (
                <span className="text-success">hit</span>
              ) : d?.cacheStatus === "skipped" ? (
                "skipped (clean build)"
              ) : d?.cacheStatus === "miss" ? (
                "miss"
              ) : (
                "—"
              )
            }
            mono
          />
          <Meta label="Framework" value={d?.framework ?? "—"} mono />
          {d?.aliases?.length ? (
            <div className="p-3">
              <div className="text-2xs uppercase tracking-wider text-subtle-foreground mb-1">Aliases</div>
              <ul className="space-y-1">
                {d.aliases.map((a) => (
                  <li key={a} className="font-mono text-xs truncate">{a}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="p-3 flex items-baseline justify-between gap-3">
      <span className="text-2xs uppercase tracking-wider text-subtle-foreground shrink-0">{label}</span>
      <span className={"text-xs text-foreground text-right truncate min-w-0 " + (mono ? "font-mono" : "")}>
        {value}
      </span>
    </div>
  );
}
